import {
  ChangeDetectorRef,
  Component,
  effect,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  untracked,
  ViewChild,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {MatButtonModule} from '@angular/material/button';
import {MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipModule,} from '@angular/material/tooltip';
import {TOOLTIP_BASE_OPTIONS} from '../shared/helpers/tooltip-defaults';
import {GraphNode, GraphNodeLink,} from '../api/interfaces/graph-node.interface';
import {NodeFormComponent} from './node-form/node-form.component';
import {MapStateService} from '../api/services/map-state.service';
import {LinkFormComponent} from './link-form/link-form.component';
import {LinkViewComponent} from './link-view/link-view.component';
import {NodeViewComponent} from './node-view/node-view.component';
import {OverlayElementFormComponent} from './overlay-element-form/overlay-element-form.component';
import {SidebarService} from './sidebar.service';
import {SettingsService} from '../api/services/setting.service';
import {CytoscapeService} from '../services/cy-services/cytoscape.service';
import {OverlayElement} from '../api/interfaces/overlay-element.interface';
import {OverlayElementApiService} from '../api/services/overlay-element.service';
import {OverlayElementManagerService} from '../services/overlay-element-manager.service';
import {
  getLinkBatchChangePropertiesFromDiff,
  getMultiElementPropertiesFromDiff,
  getNodeBatchChangePropertiesFromDiff,
  MultiUpdateEvent,
} from './dtos/node-allowed-multi-change';
import {diff} from '../shared/helpers/diff';
import {first, get, isEmpty, isEqual, isUndefined, merge, set,} from 'lodash';
import {DefaultValues, MetadataFanOut, metadataChangesBetween, sameVisibleMetadata} from '@maporium-workspace/shared';
import {REAFFIRM_ON_RELEASE} from '../shared/forms/slider-input/slider-input.component';
import {DashboardComponent} from '../dashboard/dashboard.component';
import {AppWideStateService} from '../services/app-state/app-wide-state.service';
import {OpenaiService} from '../api/services/openai.service';
import {GenerationStateService} from '../services/generation-state.service';
import {isInIframe} from '../shared/helpers/maporium.validators';
import {MapService} from '../api/services/map.service';
import {MatDialog} from '@angular/material/dialog';
import {CreditApiService} from '../api/services/credit.service';
import {hasGeneratedNewMetadata, updateInProgress, userEditedMetadata,} from '../shared/helpers/signal.helpers';
import {getFlatProps} from '../shared/helpers/object.helpers';

/**
 * Compares two values with type coercion for numeric values.
 * This handles cases where one value is a number (from node data)
 * and the other is a string (from form controls).
 * Returns true if the values are semantically equal.
 */
function isEqualWithCoercion(a: any, b: any): boolean {
  // If strictly equal, they're equal
  if (a === b) return true;

  // If both are null/undefined, they're equal
  if (a == null && b == null) return true;

  // Handle numeric comparison with type coercion
  const numA = typeof a === 'string' ? parseFloat(a) : a;
  const numB = typeof b === 'string' ? parseFloat(b) : b;

  // If both can be parsed as valid numbers, compare numerically
  if (typeof numA === 'number' && typeof numB === 'number' &&
      !isNaN(numA) && !isNaN(numB)) {
    return numA === numB;
  }

  // Fall back to strict equality (already checked above, but handles edge cases)
  return isEqual(a, b);
}

/**
 * Properties that should use numeric coercion when comparing.
 * These properties can be strings from form controls or numbers from node data.
 */
const NUMERIC_COERCION_KEYS = new Set([
  'weight',
  'properties_scale',
  'properties_opacity',
  'properties_colorOpacity',
  'properties_imageOpacity',
  'properties_imageScale',
  'properties_textFontSize',
  'properties_textMarginX',
  'properties_textMarginY',
  'properties_textRotation',
]);

/**
 * Compares two flattened property objects with type coercion for numeric fields.
 * This handles cases where numeric properties might be strings from form controls
 * or numbers from node data.
 */
function isEqualWithNumericCoercion(a: Record<string, any>, b: Record<string, any>): boolean {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  // Different number of keys means not equal
  if (keysA.length !== keysB.length) return false;

  // Check all keys exist in both objects and values are equal
  for (const key of keysA) {
    if (!(key in b)) return false;

    const valA = a[key];
    const valB = b[key];

    // Use numeric coercion for known numeric properties
    if (NUMERIC_COERCION_KEYS.has(key)) {
      if (!isEqualWithCoercion(valA, valB)) return false;
    } else {
      if (!isEqual(valA, valB)) return false;
    }
  }

  return true;
}

@Component({
  selector: 'maporium-sidebar-form',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatTooltipModule,
    NodeFormComponent,
    LinkFormComponent,
    LinkViewComponent,
    NodeViewComponent,
    OverlayElementFormComponent,
  ],
  templateUrl: './sidebar-form.component.html',
  styleUrls: ['./sidebar-form.component.scss'],
  providers: [
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: {
        ...TOOLTIP_BASE_OPTIONS,
        position: 'left',
      },
    },
    {provide: REAFFIRM_ON_RELEASE, useValue: true},
  ],
})
export class SidebarFormComponent implements OnChanges {
  @Input() readonly = false;
  @Input() focusedNode: GraphNode | undefined;
  @Input() focusedLink: GraphNodeLink | undefined;

  focusedOverlayElement: OverlayElement | undefined;

  private readonly MEANINGFUL_NODE_PROPS = [
    'id',
    'name',
    'description',
    'color',
    'type',
    'weight',
    'imageUrl',
    'excludedFromState',
    'metadata',
    'properties.borderStyle',
    'properties.soundUrl',
    'properties.scale',
    'properties.textFontSize',
    'properties.label',
    'properties.opacity',
    'properties.colorOpacity',
    'properties.imageOpacity',
    'properties.imageScale',
    'properties.textHalign',
    'properties.textValign',
    'properties.textMarginX',
    'properties.textMarginY',
    'properties.textFontColor',
    'properties.textRotation',
  ];

  @ViewChild(NodeFormComponent) nodeFormComponent?: NodeFormComponent;
  @ViewChild(LinkFormComponent) linkFormComponent?: LinkFormComponent;

  @Output() updateNode = new EventEmitter<Partial<GraphNode>>();
  @Output() updateLink = new EventEmitter<Partial<GraphNodeLink>>();
  @Output() updateMultiple = new EventEmitter<MultiUpdateEvent>();
  @Output() resetMultiple = new EventEmitter<{
    linkIds: string[];
    nodeIds: string[];
  }>();
  @Output() stateReset = new EventEmitter<{
    node?: Partial<GraphNode>;
    link?: Partial<GraphNodeLink>;
  }>();

  isEditMode = false;
  public readModeAppState = this.appWideStateService.readMode();

  private pendingNodeUpdate: Partial<GraphNode> | null = null;
  private pendingLinkUpdate: Partial<GraphNodeLink> | null = null;
  private propagatedMetadata: { id: string; list: any[] } | null = null;
  private styleTouches = new Map<string, unknown>();
  private styleTouchOwner: string | undefined;
  private styleTouchTimer: ReturnType<typeof setTimeout> | undefined;

  excludeFromStateOptions = [
    { key: 'false', value: 'included', icon: 'add_circle' },
    { key: 'true', value: 'excluded', icon: 'do_not_disturb_on' },
  ];

  constructor(
    private sidebarService: SidebarService,
    private cyService: CytoscapeService,
    private cdr: ChangeDetectorRef,
    private appWideStateService: AppWideStateService,
    private openaiService: OpenaiService,
    private mapService: MapService,
    private mapStateService: MapStateService,
    private overlayElementService: OverlayElementApiService,
    private overlayManager: OverlayElementManagerService,
    private dialog: MatDialog,
    private creditApiService: CreditApiService,
    private generationStateService: GenerationStateService,
    private settingsService: SettingsService,
  ) {
    this.sidebarService.overlayElementChanged$.subscribe(element => {
      this.focusedOverlayElement = element || undefined;
      if (element) {
        this.focusedNode = undefined;
        this.focusedLink = undefined;
      }
      this.cdr.detectChanges();
    });

    // Initialize from current signal value — covers the case where the
    // overlay-element select event fired before this component was created
    // (the Subject doesn't replay, but the signal retains the value).
    const currentOverlay = this.sidebarService.getSelectedOverlayElement();
    if (currentOverlay) {
      this.focusedOverlayElement = currentOverlay;
    }

    effect(() => {
      this.isEditMode = this.sidebarService.getEditModeSignal();
      const cy = this.cyService.cy!;

      if (!this.isEditMode) {
        // Sync form values to the focused objects before showing read mode
        this.syncFormValuesToFocusedObjects();

        setTimeout(() => {
          const data = cy
            .$('#' + (this.focusedLink?.id || this.focusedNode?.id))
            ?.data();
          const currentState = this.mapStateService.getCurrentLocalState();
          const stateData: any = first(
            data?.states?.filter(
              (s: { stateId: string | undefined }) =>
                s.stateId === currentState?.id,
            ),
          );
          if (
            stateData &&
            (stateData?.link?.excludedFromState ||
              stateData?.node?.excludedFromState)
          ) {
            this.cyService.cy?.$(':selected').unselect();
            this.focusedNode = undefined;
            this.focusedLink = undefined;
            DashboardComponent.tappedNode = null;
            DashboardComponent.tappedLink = null;
          } else {
            if (data?.excludedFromState) {
              this.cyService.cy?.$(':selected').unselect();
              this.focusedNode = undefined;
              this.focusedLink = undefined;
              DashboardComponent.tappedNode = null;
              DashboardComponent.tappedLink = null;
            }
          }
        }, 250);
      }
    });

    effect(() => {
      this.readModeAppState = this.appWideStateService.readMode();
    });

    effect(() => {
      const inProgress = updateInProgress();
      if (!inProgress) {
        untracked(() => {
          if (this.pendingNodeUpdate) {
            const pending = this.pendingNodeUpdate;
            this.pendingNodeUpdate = null;
            this.updateNode.emit(pending);
          }
          if (this.pendingLinkUpdate) {
            const pending = this.pendingLinkUpdate;
            this.pendingLinkUpdate = null;
            this.updateLink.emit(pending);
          }
        });
      }
    });
  }

  get canEdit() {
    if (this.readonly) return false;
    return this.isEditMode && (!this.readonly || !isInIframe());
  }

  get isDefaultState() {
    return this.mapStateService.getCurrentLocalState()?.id === undefined;
  }

  ngOnChanges(changes: SimpleChanges): void {
    const focusedId = this.focusedNode?.id ?? this.focusedLink?.id;
    if (this.propagatedMetadata && this.propagatedMetadata.id !== focusedId) {
      this.propagatedMetadata = null;
    }
    //@ts-ignore
    if (this.focusedLink?.isNew || this.focusedNode?.isNew) {
      this.sidebarService.enableEditMode();
    }
  }

  public onHasCommentsChange(
    element: GraphNode | GraphNodeLink | undefined,
    hasComments: boolean,
  ) {
    if (!element?.id) {
      return;
    }
    element.hasComments = hasComments;
    this.cyService.cy?.getElementById(element.id).data('hasComments', hasComments);
  }

  public onNodeUpdate(event: Partial<GraphNode>) {
    const cy = this.cyService.cy;
    if (!cy) {
      return;
    }
    const allSelected = cy.$(':selected');

    // Skip update only if BOTH: nothing is selected AND no valid event ID.
    // This allows saving changes (like newly added properties) when deselecting
    // by clicking the canvas, while still blocking truly spurious updates.
    // The event.id check ensures we don't skip legitimate saves from ngOnDestroy.
    if (allSelected.length === 0 && !event?.id) {
      return;
    }

    // Snapshot+reset so the flag scopes to exactly this emit; later typing
    // in the metadata table re-arms it.
    const metadataEditedByUser = userEditedMetadata();
    userEditedMetadata.set(false);

    if (allSelected.length > 1) {
      const difference = diff(this.focusedNode, event);
      this.dropUnchangedText(difference, this.focusedNode);

      // Create a set of properties that were actually changed by the user
      // Only include properties where the user's new value differs from the original focused node
      const intentionallyChanged = this.getIntentionallyChangedProperties(
        this.focusedNode,
        event,
      );

      // The title saves on its own debounce (node-form's nameSaveTimer) and
      // emits just {id, name}. By the time it fires the focused node can
      // already carry that name, so the diff comes back empty and the rest of
      // the selection never hears about it. The emit shape is unambiguous, so
      // take the name from it directly.
      const named = difference as Partial<GraphNode>;
      if (this.isNameOnlyEmit(event) && named.name === undefined) {
        named.name = event.name;
        intentionallyChanged?.add('name');
      }

      for (const path of this.takeStyleTouches(event?.id)) {
        const value = get(event, path);
        if (value === undefined) continue;
        if (get(difference, path) === undefined) set(difference, path, value);
        intentionallyChanged?.add(path);
      }

      const emitPayload: MultiUpdateEvent = {
        node: getNodeBatchChangePropertiesFromDiff(
          difference,
          intentionallyChanged,
        ),
        general: getMultiElementPropertiesFromDiff(difference as any, intentionallyChanged),
      };
      // Only fan metadata out across the whole selection when the user is
      // actively editing the metadata table. The raw diff also fires for
      // stale form re-emits triggered by AI/external updates of just the
      // focused element, which used to overwrite every other selected node
      // with the focused node's metadata.
      const fanOut = metadataEditedByUser ? this.metadataFanOut(this.focusedNode, event) : undefined;
      if (fanOut) {
        emitPayload.metadata = fanOut;
      }
      this.keepInformationFocused(emitPayload, difference, event);
      this.syncFocusedDescription(this.focusedNode, difference);
      this.updateMultiple.emit(emitPayload);
    } else {
      this.takeStyleTouches();
      // Use whitelist approach with flattened comparison for meaningful properties only
      // Use type-aware comparison to handle string/number mismatches for numeric properties
      const meaningfulFocusedNode = this.extractMeaningfulNodeProperties(
        this.focusedNode,
      );
      const meaningfulEvent = this.extractMeaningfulNodeProperties(event);

      if (isEqualWithNumericCoercion(meaningfulFocusedNode, meaningfulEvent)) {
        return;
      }

      // emit update for actual changes
      this.emitNodeUpdate(event);
    }
  }

  /**
   * MAP-1688: a name, description or property edit made with several elements
   * selected goes only to the focused one unless the viewer has asked for it
   * to reach the whole selection. Everything else, the styling, still fans
   * out: that is what a multiselection is for.
   */
  private keepInformationFocused(
    payload: MultiUpdateEvent,
    difference: Record<string, unknown>,
    event: Partial<GraphNode> | Partial<GraphNodeLink>,
    kind: 'node' | 'link' = 'node',
  ): void {
    if (this.settingsService.getApplyEditsToMultiselection()) return;

    const held: Record<string, unknown> = {};
    for (const key of ['name', 'description', 'metadata']) {
      if (!(key in difference)) continue;
      held[key] = (difference as Record<string, unknown>)[key];
      if (payload.general) delete (payload.general as Record<string, unknown>)[key];
      if (payload.node) delete (payload.node as Record<string, unknown>)[key];
      if (payload.link) delete (payload.link as Record<string, unknown>)[key];
    }
    if (payload.metadata) held['metadata'] = payload.metadata.source;
    delete payload.metadata;
    if (Object.keys(held).length === 0) return;

    const focused = kind === 'node' ? this.focusedNode : this.focusedLink;
    const id = (event as { id?: string })?.id ?? focused?.id;
    if (!id) return;
    if (kind === 'node') {
      this.emitNodeUpdate({ id, ...held } as Partial<GraphNode>);
    } else {
      this.emitLinkUpdate({ id, ...held } as Partial<GraphNodeLink>);
    }
  }

  public onStyleTouched(touch: { id?: string; path: string; value: unknown }, kind: 'node' | 'link') {
    if (!touch.id || (this.cyService.cy?.$(':selected').length ?? 0) < 2) {
      this.takeStyleTouches();
      return;
    }
    if (this.styleTouchOwner !== touch.id) this.takeStyleTouches();
    this.styleTouchOwner = touch.id;
    this.styleTouches.set(touch.path, touch.value);
    clearTimeout(this.styleTouchTimer);
    this.styleTouchTimer = setTimeout(() => {
      if (this.styleTouches.size === 0 || this.styleTouchOwner !== touch.id) return;
      if (kind === 'node') this.nodeFormComponent?.update();
      else this.linkFormComponent?.update();
    }, 800);
  }

  private takeStyleTouches(id?: string): string[] {
    clearTimeout(this.styleTouchTimer);
    const paths = id && this.styleTouchOwner === id ? [...this.styleTouches.keys()] : [];
    this.styleTouches.clear();
    this.styleTouchOwner = undefined;
    return paths;
  }

  private dropUnchangedText(difference: Record<string, any>, original: Partial<GraphNode> | Partial<GraphNodeLink> | undefined): void {
    for (const key of ['name', 'description'] as const) {
      if (key in difference && (original?.[key] ?? '') === (difference[key] ?? '')) {
        delete difference[key];
      }
    }
  }

  private syncFocusedDescription(
    focused: Partial<GraphNode> | Partial<GraphNodeLink> | undefined,
    difference: Record<string, any>,
  ): void {
    if (focused && 'description' in difference) {
      focused.description = difference['description'];
    }
  }

  private metadataFanOut(
    focused: Partial<GraphNode> | Partial<GraphNodeLink> | undefined,
    event: Partial<GraphNode> | Partial<GraphNodeLink>,
  ): MetadataFanOut | undefined {
    if (!focused?.id || !Array.isArray(event?.metadata)) return undefined;
    const before = this.propagatedMetadata?.id === focused.id
      ? this.propagatedMetadata.list
      : (focused.metadata ?? []);
    if (sameVisibleMetadata(before, event.metadata)) return undefined;
    this.propagatedMetadata = {id: focused.id, list: event.metadata};
    return {
      sourceId: focused.id,
      source: event.metadata,
      changes: metadataChangesBetween(before, event.metadata),
    };
  }

  /** node-form's own title save: an id and a name, nothing else. */
  private isNameOnlyEmit(event: Partial<GraphNode>): boolean {
    const keys = Object.keys(event ?? {});
    return (
      event?.name !== undefined &&
      keys.length <= 2 &&
      keys.every((key) => key === 'id' || key === 'name')
    );
  }

  onReset($event: {
    node?: Partial<GraphNode>;
    link?: Partial<GraphNodeLink>;
  }) {
    if (!this.cyService.cy) {
      return;
    }
    const selectedElements = this.cyService.cy.$(':selected');
    // Are we in multiselect ?
    if (this.cyService.cy.$(':selected').length > 1) {
      const resetPayload = {
        linkIds: selectedElements.filter((e) => e.isEdge()).map((e) => e.id()),
        nodeIds: selectedElements.filter((e) => e.isNode()).map((n) => n.id()),
      };
      this.resetMultiple.emit(resetPayload);
    } else {
      this.stateReset.emit($event);
    }
  }

  public onLinkUpdate(event: Partial<GraphNodeLink>) {
    const cy = this.cyService.cy;
    if (!cy) {
      return;
    }
    const difference = diff(this.focusedLink, event);
    const allSelected = cy.$(':selected');

    // Same metadata-broadcast gate as onNodeUpdate.
    const metadataEditedByUser = userEditedMetadata();
    userEditedMetadata.set(false);

    if (allSelected.length > 1) {
      this.dropUnchangedText(difference, this.focusedLink);
      for (const path of this.takeStyleTouches(event?.id)) {
        const value = get(event, path);
        if (value !== undefined && get(difference, path) === undefined) set(difference, path, value);
      }
      const emitPayload: MultiUpdateEvent = {
        general: getMultiElementPropertiesFromDiff(difference),
        link: getLinkBatchChangePropertiesFromDiff(difference),
      };
      const fanOut = metadataEditedByUser ? this.metadataFanOut(this.focusedLink, event) : undefined;
      if (fanOut) {
        emitPayload.metadata = fanOut;
      }
      this.keepInformationFocused(emitPayload, difference, event, 'link');
      this.syncFocusedDescription(this.focusedLink, difference);
      this.updateMultiple.emit(emitPayload);
    } else {
      this.takeStyleTouches();
      this.emitLinkUpdate(event);
    }
  }

  public onMetaExtend() {
    const isNodeExtension = !isEmpty(this.focusedNode);

    hasGeneratedNewMetadata.set(true);
    const signalData = this.openaiService.extendedMetadataSignal();

    // If we have a result from a bubble-based generation for node/link, use it
    if (signalData && (signalData.source === 'node' || signalData.source === 'link')) {
      this.openaiService.extendedMetadataSignal.set(null);
      this.applyMetadataResult(signalData.result as any, isNodeExtension);
      return;
    }

    // No result in signal - make the API call directly (Creator tier direct click)
    const entity = isNodeExtension ? this.focusedNode : this.focusedLink;
    if (!entity) {
      return;
    }

    const currentMap = this.mapService.getCurrentSelectedMapFromStore();
    if (!currentMap?.id) {
      return;
    }

    this.creditApiService.ensureCredits(this.dialog, 'properties').subscribe(ok => {
      if (!ok) return;

      const dto = {
        title: entity.name || '',
        description: entity.description || '',
        metadata: entity.metadata || [],
      };

      // Default options for Creator tier: generate description if missing, extend properties
      const defaultOptions = {
        description: entity.description ? 'maintain' : 'refresh',
        properties: this.hasEmptyProperties(entity.metadata) ? 'complete' : 'extend',
        longPressOnly: false,
      };

      // Drives the generate icon animation (MAP-1402).
      const taskId = `sidebar:${entity?.id ?? 'na'}:${Date.now()}`;
      this.generationStateService.startPropertyGeneration(taskId);
      this.openaiService
        .extendMetadata(dto as any, currentMap.id, defaultOptions as any)
        .subscribe({
          next: (apiResult) => {
            this.generationStateService.endPropertyGeneration(taskId);
            if (apiResult) {
              this.applyMetadataResult(apiResult, isNodeExtension);
            }
          },
          error: (error) => {
            this.generationStateService.endPropertyGeneration(taskId);
            console.error('Failed to generate metadata:', error);
          },
        });
    });
  }

  private hasEmptyProperties(metadata: any[] | undefined): boolean {
    if (!metadata || metadata.length === 0) {
      return false;
    }
    return metadata.some((meta: any) => !meta.value || meta.value.trim?.() === '');
  }

  private applyMetadataResult(result: any, isNodeExtension: boolean) {
    // MAP-1447: AI-generated metadata is computed from the focused element's
    // own title/description/metadata, so it must NOT propagate to other
    // selected elements. Bypass onNodeUpdate/onLinkUpdate (which route through
    // the multi-select updateMultiple emit) and update only the focused one.
    if (isNodeExtension) {
      const merged = merge({}, this.focusedNode, {
        description: result.description,
        metadata: result.metadata,
      });
      this.emitNodeUpdate(merged);
      if (this.focusedNode) {
        const node = { ...this.focusedNode };
        node.metadata = result.metadata;
        node.description = result.description;
        this.focusedNode = node;
        this.cdr.detectChanges();
      }
    } else {
      const merged = merge({}, this.focusedLink, {
        description: result.description,
        metadata: result.metadata,
      });
      this.emitLinkUpdate(merged);
      if (this.focusedLink) {
        const link = { ...this.focusedLink };
        link.metadata = result.metadata;
        link.description = result.description;
        this.focusedLink = link;
        this.cdr.detectChanges();
      }
    }
  }

  private emitNodeUpdate(nodeData: Partial<GraphNode>) {
    if (!updateInProgress()) {
      this.updateNode.emit(nodeData);
    } else {
      this.pendingNodeUpdate = nodeData;
    }
  }

  private emitLinkUpdate(linkData: Partial<GraphNodeLink>) {
    if (!updateInProgress()) {
      this.updateLink.emit(linkData);
    } else {
      this.pendingLinkUpdate = linkData;
    }
  }

  /**
   * Determines which single property was actually changed by the user.
   * Since humans can only interact with one form control at a time, there should
   * only be ONE property change per user interaction. If multiple properties changed,
   * it's likely due to form patching from different selected nodes (race condition).
   */
  private getIntentionallyChangedProperties(
    original: Partial<GraphNode> | undefined,
    updated: Partial<GraphNode>,
  ): Set<string> | undefined {
    // If original is undefined (e.g., newly created nodes), return undefined
    // to indicate all changed properties from diff should be allowed through
    if (!original) {
      return undefined;
    }

    const intentionallyChanged = new Set<string>();

    const changedProperties: string[] = [];

    // Check top-level properties
    if (!isEqual(original.color, updated.color)) {
      changedProperties.push('color');
    }

    if (!isEqual(original.name, updated.name)) {
      changedProperties.push('name');
    }

    if ((original.description ?? '') !== (updated.description ?? '')) {
      changedProperties.push('description');
    }

    if (!isEqualWithCoercion(original.weight, updated.weight)) {
      changedProperties.push('weight');
    }

    if (!isEqual(original.type, updated.type)) {
      changedProperties.push('type');
    }

    if (!isEqual(original.imageUrl, updated.imageUrl)) {
      changedProperties.push('imageUrl');
    }

    if (!isEqual(original.excludedFromState, updated.excludedFromState)) {
      changedProperties.push('excludedFromState');
    }

    // Check nested properties
    // Handle case where original.properties might be undefined (e.g., for newly created nodes)
    const origProps: any = original.properties || {};
    const updProps: any = updated.properties || {};

    // Helper to check if a property was intentionally changed
    // Tracks changes when:
    // 1. Original was defined and different from updated, OR
    // 2. Original was undefined but updated differs from the default (user chose non-default)
    const hasIntentionalChange = (orig: any, upd: any, useCoercion = false, defaultValue?: any): boolean => {
      // If values are equal, no change
      if (useCoercion ? isEqualWithCoercion(orig, upd) : isEqual(orig, upd)) {
        return false;
      }
      // If original was defined, user changed FROM something
      if (orig != null) {
        return true;
      }
      // Original was undefined - only track if updated differs from default
      // This prevents false positives when form emits defaults for undefined properties
      if (defaultValue !== undefined && upd != null) {
        return useCoercion ? !isEqualWithCoercion(upd, defaultValue) : !isEqual(upd, defaultValue);
      }
      // No default to compare, track if updated is defined
      return upd != null;
    };

    if (hasIntentionalChange(origProps.scale, updProps.scale, true, DefaultValues.node.properties.scale)) {
      changedProperties.push('properties.scale');
    }

    if (hasIntentionalChange(origProps.opacity, updProps.opacity, true, DefaultValues.node.properties.opacity)) {
      changedProperties.push('properties.opacity');
    }

    if (hasIntentionalChange(origProps.colorOpacity, updProps.colorOpacity, true, DefaultValues.node.properties.colorOpacity)) {
      changedProperties.push('properties.colorOpacity');
    }

    if (hasIntentionalChange(origProps.imageOpacity, updProps.imageOpacity, true, DefaultValues.node.properties.imageOpacity)) {
      changedProperties.push('properties.imageOpacity');
    }

    if (hasIntentionalChange(origProps.imageScale, updProps.imageScale, true, DefaultValues.node.properties.imageScale)) {
      changedProperties.push('properties.imageScale');
    }

    if (hasIntentionalChange(origProps.textFontSize, updProps.textFontSize, true, DefaultValues.node.properties.textFontSize)) {
      changedProperties.push('properties.textFontSize');
    }

    if (hasIntentionalChange(origProps.borderStyle, updProps.borderStyle, false, DefaultValues.node.properties.borderStyle)) {
      changedProperties.push('properties.borderStyle');
    }

    if (hasIntentionalChange(origProps.textMarginX, updProps.textMarginX, true, DefaultValues.node.properties.textMarginX)) {
      changedProperties.push('properties.textMarginX');
    }

    if (hasIntentionalChange(origProps.textMarginY, updProps.textMarginY, true, DefaultValues.node.properties.textMarginY)) {
      changedProperties.push('properties.textMarginY');
    }

    if (hasIntentionalChange(origProps.textValign, updProps.textValign, false, DefaultValues.node.properties.textValign)) {
      changedProperties.push('properties.textValign');
    }

    if (hasIntentionalChange(origProps.textHalign, updProps.textHalign, false, DefaultValues.node.properties.textHalign)) {
      changedProperties.push('properties.textHalign');
    }

    if (hasIntentionalChange(origProps.soundUrl, updProps.soundUrl)) {
      changedProperties.push('properties.soundUrl');
    }

    if (hasIntentionalChange(origProps.label, updProps.label)) {
      changedProperties.push('properties.label');
    }

    // Include all changed properties - let the diff determine what actually changed
    changedProperties.forEach((prop) => {
      intentionallyChanged.add(prop);
    });

    return intentionallyChanged;
  }

  private extractMeaningfulNodeProperties(
    node: Partial<GraphNode> | undefined,
  ): Record<string, any> {
    return getFlatProps(this.MEANINGFUL_NODE_PROPS, node);
  }

  public onOverlayElementUpdate(element: OverlayElement) {
    // Optimistic update: apply changes to Konva shape immediately
    // for real-time visual feedback (don't wait for API roundtrip).
    // Pass optimistic=true so that in non-base states, the local state entry
    // is updated instead of corrupting the base data.
    this.overlayManager.updateElement(element, true);
    this.focusedOverlayElement = element;

    this.overlayElementService.update(element).subscribe(updated => {
      this.overlayManager.updateElement(updated);
      this.focusedOverlayElement = updated;
    });
  }

  public onOverlayElementDelete(elementId: string) {
    const dashRef = (window as any).__dashboardComponentRef;
    if (dashRef?.deleteOverlayElement) {
      dashRef.deleteOverlayElement(elementId);
    }
  }

  private syncFormValuesToFocusedObjects() {
    // Sync node form values to focusedNode
    if (this.focusedNode && this.nodeFormComponent) {
      try {
        const formValues = this.nodeFormComponent.formGroup.getRawValue();
        const metadataFormData =
          this.nodeFormComponent.formGroup.get('metadata')?.getRawValue()
            ?.pairs ||
          this.nodeFormComponent.formGroup.get('metadata')?.getRawValue();

        if (metadataFormData && Array.isArray(metadataFormData)) {
          this.focusedNode = {
            ...this.focusedNode,
            name: formValues.name || undefined,
            description: formValues.description || undefined,
            metadata: metadataFormData,
          };
        }
      } catch (error) {
        console.warn('Failed to sync node form values:', error);
      }
    }

    // Sync link form values to focusedLink
    if (this.focusedLink && this.linkFormComponent) {
      try {
        const formValues = this.linkFormComponent.formGroup.getRawValue();
        const metadataFormData =
          this.linkFormComponent.formGroup.get('metadata')?.getRawValue()
            ?.pairs ||
          this.linkFormComponent.formGroup.get('metadata')?.getRawValue();

        if (metadataFormData && Array.isArray(metadataFormData)) {
          this.focusedLink = {
            ...this.focusedLink,
            name: formValues.name || undefined,
            description: formValues.description || undefined,
            metadata: metadataFormData,
          };
        }
      } catch (error) {
        console.warn('Failed to sync link form values:', error);
      }
    }
  }
}
